import NotificationsIcon from '@mui/icons-material/Notifications';
import Badge from '@mui/material/Badge';
import IconButton from '@mui/material/IconButton';
import Link from '@mui/material/Link';
import Popover from '@mui/material/Popover';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useId, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import { ROLES } from '@/auth/roles';
import { useAuth } from '@/auth/useAuth';
import { useViewingAs } from '@/auth/useViewingAs';

import { ROUTES } from '../routes';

/**
 * The bell, and a panel that says what it is for.
 *
 * In-app notifications are in neither client document, and a bell that did
 * nothing when pressed left people wondering whether it was broken. It now
 * says so plainly, and points an organization's own people - and staff
 * viewing one - to where push notifications to supporters are managed.
 * Staff in their own area manage no notifications, so they get no link.
 *
 * The badge stays, invisible, for the day there is a count to show.
 */
export function NotificationsPanel() {
  const { user } = useAuth();
  const { viewing } = useViewingAs();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const headingId = useId();
  const open = Boolean(anchor);
  const offersLink = user?.role !== ROLES.ACTIVIST_STAFF || Boolean(viewing);

  return (
    <>
      <Tooltip title="Notifications">
        <IconButton
          aria-label="Notifications"
          aria-haspopup="dialog"
          aria-expanded={open}
          onClick={(event) => setAnchor(event.currentTarget)}
        >
          <Badge color="error" variant="dot" invisible>
            <NotificationsIcon />
          </Badge>
        </IconButton>
      </Tooltip>

      {/* Popover returns focus to the bell when it closes, on Escape and on
          a click elsewhere alike. */}
      <Popover
        open={open}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: {
            role: 'dialog',
            'aria-labelledby': headingId,
            sx: { p: 2, width: 'min(320px, calc(100vw - 32px))' },
          },
        }}
      >
        <Typography
          id={headingId}
          variant="subtitle1"
          component="h2"
          sx={{ fontWeight: 600 }}
        >
          Notifications
        </Typography>
        <Typography variant="body2" sx={{ mt: 1 }}>
          In-app notifications are not part of this release.
        </Typography>
        {offersLink ? (
          <Typography variant="body2" sx={{ mt: 1 }}>
            Push notifications to your supporters are managed in the{' '}
            <Link
              component={RouterLink}
              to={ROUTES.notifications.all}
              onClick={() => setAnchor(null)}
            >
              Notifications section
            </Link>
            .
          </Typography>
        ) : null}
      </Popover>
    </>
  );
}
